/** @format */

import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Badge, Grid, styled, Button, Box } from '@mui/material';
// import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { faBullseye } from '@fortawesome/pro-solid-svg-icons';
import { faBellOn } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import NewTooltip from '../../misc/NewTooltip';

const StyledMenuButtonIcon = styled(FontAwesomeIcon, {
  shouldForwardProp: (propName) => propName !== 'light',
})(({ theme, light }) => ({
  marginRight: theme.spacing(1),
  color: light ? '#408b6a' : undefined,
}));

const StyledBadgeRed = styled(Badge)(({ theme }) => ({
  '& > .MuiBadge-badge': {
    right: -9,
    top: '0%',
    border: `2px solid ${theme.palette.background.paper}`,
    padding: '0 4px',
    backgroundColor: 'red',
    color: 'white',
  },
}));

const StyledBadgeYellow = styled(Badge)(({ theme }) => ({
  '& > .MuiBadge-badge': {
    right: -9,
    top: '50%',
    border: `2px solid ${theme.palette.background.paper}`,
    padding: '0 4px',
    backgroundColor: 'yellow',
    color: 'black',
  },
}));

const StyledBadgeGreen = styled(Badge)(({ theme }) => ({
  '& > .MuiBadge-badge': {
    right: -9,
    top: '100%',
    border: `2px solid ${theme.palette.background.paper}`,
    padding: '0 4px',
    backgroundColor: 'green',
    color: 'white',
  },
}));

const countOverdueLeads = (leads) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return leads.filter((lead) => {
    const dueDate = new Date(lead.follow_up_task?.due_date);
    return (
      dueDate < today && lead.current_follow_up_status?.is_no_follow_up !== true
    );
  }).length;
};

const countSameDayOverdueLeads = (leads) => {
  const today = new Date().toDateString();
  return leads.filter((lead) => {
    const dueDate = new Date(lead.follow_up_task?.due_date).toDateString();
    return (
      dueDate === today &&
      lead.current_follow_up_status?.is_no_follow_up !== true
    );
  }).length;
};

const countDueThisWeekLeads = (leads) => {
  const today = new Date();
  const endOfWeek = new Date(today);
  endOfWeek.setDate(today.getDate() + (7 - today.getDay()));

  return leads.filter((lead) => {
    const dueDate = new Date(lead.follow_up_task?.due_date);
    return (
      dueDate > today &&
      dueDate <= endOfWeek &&
      lead.current_follow_up_status?.is_no_follow_up !== true
    );
  }).length;
};

function FollowUpToggleButtonGroup({ leads, loading, view, isProjectManager }) {
  const overdueLeadsCount = useMemo(() => countOverdueLeads(leads), [leads]);
  const sameDayOverdueLeadsCount = useMemo(
    () => countSameDayOverdueLeads(leads),
    [leads],
  );
  const dueThisWeekLeadsCount = useMemo(
    () => countDueThisWeekLeads(leads),
    [leads],
  );

  const buttonGroup = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        width: 'fit-content',
        height: '36px',
      }}
    >
      <NewTooltip content="Leads">
        <Button
          color={
            window.location.href.includes('leads') &&
            !window.location.href.includes('leads-followup')
              ? 'primary'
              : 'default'
          }
          variant="contained"
          to="/leads"
          component={Link}
          disabled={loading}
          sx={{
            fontColor: '#408b6a',
            height: '100%',
            borderRight: '1px solid #408b6a',
            borderTopRightRadius: 0,
            borderBottomRightRadius: 0,
          }}
        >
          <StyledMenuButtonIcon
            icon={faBullseye}
            light={
              !window.location.href.includes('leads') ||
              window.location.href.includes('leads-followup')
            }
          />
        </Button>
      </NewTooltip>
      <NewTooltip content="Relance">
        <Button
          color={
            window.location.href.includes('leads-followup')
              ? 'primary'
              : 'default'
          }
          variant="contained"
          to="/leads-followup"
          component={Link}
          disabled={loading}
          sx={{
            fontColor: '#408b6a',
            height: '100%',
            borderTopLeftRadius: 0,
            borderBottomLeftRadius: 0,
          }}
        >
          <StyledMenuButtonIcon
            icon={faBellOn}
            light={!window.location.href.includes('leads-followup')}
          />
        </Button>
      </NewTooltip>
    </Box>
  );

  return (
    <Grid item container justifyContent="center" sx={{ mr: 10 }}>
      {isProjectManager ? (
        <StyledBadgeRed
          badgeContent={overdueLeadsCount}
          invisible={overdueLeadsCount === 0}
        >
          <StyledBadgeYellow
            badgeContent={sameDayOverdueLeadsCount}
            invisible={sameDayOverdueLeadsCount === 0}
          >
            <StyledBadgeGreen
              badgeContent={dueThisWeekLeadsCount}
              invisible={dueThisWeekLeadsCount === 0}
            >
              {buttonGroup}
            </StyledBadgeGreen>
          </StyledBadgeYellow>
        </StyledBadgeRed>
      ) : (
        buttonGroup
      )}
    </Grid>
  );
}

FollowUpToggleButtonGroup.propTypes = {
  leads: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  loading: PropTypes.bool.isRequired,
  view: PropTypes.string.isRequired,
  isProjectManager: PropTypes.bool.isRequired,
};

export default FollowUpToggleButtonGroup;
